<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { TimeFrame, WorkflowDefinition } from "~/api";
import { showError } from "~/utils/handleAPIError";
import SortableTimeFrames from "./SortableTimeFrames.vue";

// @ts-expect-error - error
const localWorkflowDefinition = ref<WorkflowDefinition>({});

const props = defineProps<{
  workflowDefinition: WorkflowDefinition;
}>();

const newTimeFrame = ref<TimeFrame>({
  code: "",
  label: "",
  startDate: undefined,
  endDate: undefined,
});
const isEditing = ref(false);
const selectedTimeFrame = ref<TimeFrame>();

const startDate = ref<Date | undefined>(undefined);
const endDate = ref<Date | undefined>(undefined);

const editTimeFrame = (timeFrame: TimeFrame) => {
  isEditing.value = true;
  selectedTimeFrame.value = { ...timeFrame };
  newTimeFrame.value = { ...timeFrame };
  startDate.value = timeFrame.startDate
    ? new Date(timeFrame.startDate as any)
    : undefined;
  endDate.value = timeFrame.endDate
    ? new Date(timeFrame.endDate as any)
    : undefined;
};

const saveNeeded = computed(() => {
  return (
    newTimeFrame.value.code !== selectedTimeFrame.value?.code ||
    newTimeFrame.value.label !== selectedTimeFrame.value.label ||
    startDate.value !== selectedTimeFrame.value.startDate ||
    endDate.value !== selectedTimeFrame.value.endDate
  );
});

const resetInputs = () => {
  isEditing.value = false;
  selectedTimeFrame.value = undefined;
  newTimeFrame.value = {
    code: "",
    label: "",
    startDate: undefined,
    endDate: undefined,
  };
  startDate.value = undefined;
  endDate.value = undefined;
};

const removeSpaces = () => {
  if (newTimeFrame.value.code) {
    newTimeFrame.value.code = newTimeFrame.value.code.replace(/\s+/g, "");
  }
};

// helper func to check for overlapping time frames
const overlaps = (a: TimeFrame, b: TimeFrame): boolean => {
  if (!a.startDate || !a.endDate || !b.startDate || !b.endDate) return false;
  const aStart = new Date(a.startDate as any).getTime();
  const aEnd = new Date(a.endDate as any).getTime();
  const bStart = new Date(b.startDate as any).getTime();
  const bEnd = new Date(b.endDate as any).getTime();
  return aStart < bEnd && bStart < aEnd;
};

const updateTimeFrame = async () => {
  const code = newTimeFrame.value.code?.trim();
  const label = newTimeFrame.value.label?.trim();

  newTimeFrame.value.startDate = startDate.value
    ? (startDate.value as Date).toISOString()
    : undefined;
  newTimeFrame.value.endDate = endDate.value
    ? (endDate.value as Date).toISOString()
    : undefined;

  if (!label) {
    showError("Label is required");
    return;
  }

  const timeFrames = localWorkflowDefinition.value.timeFrames || [];

  const isDuplicateCode = timeFrames.some(
    (tf) =>
      tf.code?.toLowerCase() === code?.toLowerCase() &&
      tf.code !== selectedTimeFrame.value?.code,
  );

  if (isDuplicateCode) {
    showError(`Time Frame with code ${code} already exists`);
    return;
  }

  if (!code && label) {
    newTimeFrame.value.code = label.replace(/\s+/g, "");
  }

  // Create a pending time frame object - check if it overlaps with other time frames
  const pendingTimeFrame: TimeFrame = {
    code: newTimeFrame.value.code,
    label: newTimeFrame.value.label,
    startDate: newTimeFrame.value.startDate,
    endDate: newTimeFrame.value.endDate,
  };
  if (pendingTimeFrame.code?.toLowerCase() !== "default") {
    const others = timeFrames.filter(
      (tf) =>
        tf.code?.toLowerCase() !== "default" &&
        tf.code !== selectedTimeFrame.value?.code,
    );
    const hasOverlap = others.some((tf) => overlaps(pendingTimeFrame, tf));
    if (hasOverlap) {
      showError("Time frame overlaps with an existing time frame.");
      return;
    }
  }

  if (!selectedTimeFrame.value) {
    if (
      localWorkflowDefinition.value.timeFrames.find(
        (tf) =>
          tf.code?.toLowerCase() === newTimeFrame.value.code?.toLowerCase(),
      )
    ) {
      showError(
        `Time Frame with code "${newTimeFrame.value.code}" already exists`,
      );
      return;
    }

    if (!localWorkflowDefinition.value.timeFrames) {
      localWorkflowDefinition.value.timeFrames = [];
    }
    localWorkflowDefinition.value.timeFrames.push({
      code: newTimeFrame.value.code,
      label: newTimeFrame.value.label,
      startDate: newTimeFrame.value.startDate,
      endDate: newTimeFrame.value.endDate,
    });
  } else {
    const timeFrameIndex = localWorkflowDefinition.value.timeFrames?.findIndex(
      (tf) => tf.code === selectedTimeFrame.value?.code,
    );
    if (timeFrameIndex !== -1) {
      localWorkflowDefinition.value.timeFrames[timeFrameIndex] = {
        ...newTimeFrame.value,
      };
    }
  }
  resetInputs();
};

const deleteTimeFrame = () => {
  const timeFrames = localWorkflowDefinition.value.timeFrames;

  const timeFrameIndex = timeFrames.findIndex(
    (tf) => tf.code === selectedTimeFrame.value?.code,
  );

  if (timeFrameIndex !== -1) {
    timeFrames.splice(timeFrameIndex, 1);
    localWorkflowDefinition.value.timeFrames = timeFrames;
    resetInputs();
  }
};

// set default time frame to blank
onMounted(() => {
  localWorkflowDefinition.value = props.workflowDefinition;
  if (
    !localWorkflowDefinition.value.timeFrames ||
    localWorkflowDefinition.value.timeFrames.length === 0
  ) {
    localWorkflowDefinition.value.timeFrames = [
      {
        code: "default",
        label: "Default",
        startDate: undefined,
        endDate: undefined,
      },
    ];
  }
});
</script>

<template>
  <div class="wfd-time-frames">
    {{ selectedTimeFrame }}
    <SortableTimeFrames
      v-if="localWorkflowDefinition.timeFrames"
      :key="localWorkflowDefinition.timeFrames?.length"
      class="sortable-time-frames"
      :time-frame-list="localWorkflowDefinition.timeFrames"
      @editing-time-frame="editTimeFrame"
    />
    <div class="time-frame-editor">
      {{ selectedTimeFrame ? "Edit" : "Add" }}
      <el-input
        v-model="newTimeFrame.label"
        draggable
        size="large"
        placeholder="Display Label"
        class="tf-editor-field"
        @keyup.enter="updateTimeFrame"
      />
      <el-input
        v-model="newTimeFrame.code"
        draggable
        placeholder="Code"
        class="tf-editor-field"
        @input="removeSpaces"
        @keyup.enter="updateTimeFrame()"
      />
      <el-date-picker
        v-model="startDate"
        type="datetime"
        draggable
        size="large"
        placeholder="Start Date/Time"
        class="tf-date-field"
        @keyup.enter="updateTimeFrame()"
      />
      <el-date-picker
        v-model="endDate"
        type="datetime"
        draggable
        size="large"
        placeholder="End Date/Time"
        :default-time="new Date(1900, 0, 1, 23, 59, 59)"
        class="tf-date-field"
        @keyup.enter="updateTimeFrame()"
      />
      <el-button
        class="add"
        :disabled="!(newTimeFrame.label && saveNeeded)"
        @click="updateTimeFrame"
      >
        {{ isEditing ? "Save" : "Add" }}
      </el-button>

      <el-button
        v-if="isEditing"
        plain
        type="danger"
        class="add"
        @click="resetInputs"
        >Cancel</el-button
      >

      <el-button
        v-if="isEditing && localWorkflowDefinition.timeFrames.length > 1"
        class="save"
        type="danger"
        @click="deleteTimeFrame"
      >
        Delete
      </el-button>
    </div>
  </div>
</template>

<style lang="less">
.wfd-time-frames {
  .time-frame-editor {
    display: flex;
    gap: 1em;
    margin: 5px 30px 0 0;
    align-items: center;
    .tf-editor-field {
      width: 140px;
    }
    .tf-date-field {
      width: 210px;
    }
  }
  .sortable-time-frames {
    flex-grow: 1;
  }
}
</style>
